import React from 'react';
import { Copy } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getStatusColors } from './StatusColors';

interface OrderPageHeaderProps {
  platformCode: string;
  createdAt: string;
  statusSlug: string;
  statusTitle: string;
  previousStatus?: string;
  orderTypeBadge?: string;
  activeSegment: 'details' | 'updates';
  onSegmentChange: (seg: 'details' | 'updates') => void;
  onCopyOrderId: () => void;
  /** Action buttons rendered next to the status pill (Ticket, Ship Now, kebab) */
  actions?: React.ReactNode;
  /** BOXY-378 — e.g. the "was partially delivered" badge, under the date. */
  statusNote?: React.ReactNode;
  /** BOXY-49 — show the Updates segment (merchant flag `order_updates_tab`). */
  showUpdatesTab?: boolean;
}

const segmentClass = (active: boolean) =>
  `px-5 pb-2.5 text-[13px] font-semibold transition-all border-b-2 -mb-px cursor-pointer bg-transparent border-0 [border-bottom-style:solid] ${
    active
      ? 'border-sd-primary text-sd-foreground'
      : 'border-transparent text-sd-muted-foreground hover:text-sd-foreground'
  }`;

export function OrderPageHeader({
  platformCode,
  createdAt,
  statusSlug,
  statusTitle,
  previousStatus,
  orderTypeBadge,
  activeSegment,
  onSegmentChange,
  onCopyOrderId,
  actions,
  statusNote,
  showUpdatesTab = false,
}: OrderPageHeaderProps) {
  const { t } = useTranslation();
  const statusColors = getStatusColors(statusSlug);

  return (
    <div>
      {/* Order ID row */}
      <div className='flex items-start justify-between gap-3'>
        {/* Left: ID + date */}
        <div>
          <div className='flex items-center gap-2'>
            <span className='text-xl font-bold tracking-tight'>{platformCode}</span>
            <button
              onClick={onCopyOrderId}
              className='p-1 rounded-md hover:bg-sd-muted transition-colors cursor-pointer bg-transparent border-0 text-sd-muted-foreground hover:text-sd-foreground'
              aria-label={String(t('copy'))}
            >
              <Copy className='h-4 w-4' strokeWidth={2} />
            </button>
          </div>
          <p className='text-[12px] text-sd-muted-foreground mt-1 m-0'>{createdAt}</p>
          {statusNote ? <div className='mt-2'>{statusNote}</div> : null}
        </div>

        {/* Right: status badge(s) */}
        <div className='flex items-center gap-2 flex-shrink-0 mt-0.5 flex-wrap justify-end'>
          {orderTypeBadge && (
            <span
              className='px-2 py-0.5 rounded-[0.35rem] border border-solid text-[10px] font-semibold'
              style={{
                borderColor: 'rgba(245,158,11,0.3)',
                backgroundColor: 'rgba(245,158,11,0.08)',
                color: 'rgb(180,115,20)',
              }}
            >
              {orderTypeBadge}
            </span>
          )}
          {previousStatus && (
            <span className='px-2 py-0.5 rounded-[0.35rem] border border-sd-border bg-sd-muted text-[10px] font-semibold text-sd-muted-foreground'>
              {previousStatus}
            </span>
          )}
          <span
            className='rounded-full px-3 py-1 text-xs font-semibold'
            style={{ backgroundColor: statusColors.bg, color: statusColors.text }}
          >
            {statusTitle}
          </span>
          {actions}
        </div>
      </div>

      {/* Segment tabs */}
      <div className='flex w-full border-b border-sd-border mb-5 mt-5'>
        <button
          className={segmentClass(activeSegment === 'details')}
          onClick={() => onSegmentChange('details')}
        >
          {String(t('order_details_tab'))}
        </button>
        {/* BOXY-49 — the Updates tab, behind the merchant's `order_updates_tab` flag. */}
        {showUpdatesTab ? (
          <button
            className={segmentClass(activeSegment === 'updates')}
            onClick={() => onSegmentChange('updates')}
          >
            {String(t('updates_tab'))}
          </button>
        ) : null}
      </div>
    </div>
  );
}
